import {
  Button,
  Divider,
  HStack,
  Icon,
  IconButton,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import PulseIcon from "components/PulseIcon";
import { format, formatDistance, formatDistanceToNow } from "date-fns";
import { useMemberAuthStore } from "modules/members/auth";
import { humanDuration, inspecto } from "modules/utils";
import { FiArrowLeft, FiCheck, FiCheckCircle, FiXCircle } from "react-icons/fi";
import { TbFingerprint } from "react-icons/tb";
import { useParams } from "react-router-dom";
import SignedBadge from "components/passkeys/SignedBadge";
import { approvalRequestPath } from "pages/Members/StepUp/navigation";
import { useApprovalSigning } from "pages/Members/StepUp/useApprovalSigning";
import { MF_ACCESS } from "state/members/access_v2/catalogs";
import PageSpinner from "ui/PageSpinner";
import RequestLines from "../Catalogs/RequestLines";

const ApprovalRequest = () => {
  const { id } = useParams();
  const request = MF_ACCESS.useGetApprovalRequest(id);
  const store = useMemberAuthStore();
  const { mutate: decide, isLoading } = MF_ACCESS.useDecideAccessRequest(
    id as string,
  );
  const { isOpen: approveOpen, onToggle: toggleApprove } = useDisclosure();
  const { isOpen: denyOpen, onToggle: toggleDeny } = useDisclosure();
  const toast = useToast();
  const { sign, isStarting } = useApprovalSigning();
  // A stage that needs a passkey is approved on the signing page, which shows
  // exactly what is being approved. Deny never needs one and stays here.
  const needsPasskey = Boolean(request?.currentStageRequiresPasskey);

  const onApproveWithPasskey = () => {
    if (!request || !id) return;
    sign({ requestId: request.id, assigneeId: id, returnTo: approvalRequestPath(id) });
  };

  const onDecide = (decision: "approve" | "deny") => {
    request &&
      decide(
        inspecto({
          requestId: request.id,
          decision,
          actionInstanceId: request?.approval.id,
          actionAssigneeId: id!,
        }),
      ).then(() =>
        toast({
          status: "success",
          variant: "subtle",
          title: decision === "approve" ? "Request approved" : "Request denied",
        }),
      );
  };

  if (!request && store.accessToken) {
    return (
      <VStack
        pb="200px"
        w="full"
        minH="100vh"
        bg="gray.50"
        pt={["50px", "100px"]}
        spacing={10}
      >
        <Text variant="xs.d.sb" textAlign="center">
          Request not found
        </Text>
        <Text variant="lil" textAlign="center">
          The request you're looking for no longer exists
        </Text>
      </VStack>
    );
  } else if (!store.accessToken) {
    return <PageSpinner />;
  }

  if (!request) {
    return null;
  }

  return (
    <VStack
      pb="200px"
      w="full"
      minH="100vh"
      bg="gray.50"
      pt={["50px", "100px"]}
      spacing={10}
    >
      <VStack
        bg="white"
        borderRadius="8px"
        border="1px solid"
        borderColor="gray.200"
        shadow="lg"
        align="flex-start"
        w="full"
        p={[4, 8]}
        maxW={["full", "600px"]}
        spacing={8}
      >
        <HStack w="full" align="flex-start" spacing={4}>
          <PulseIcon icon={FiCheck} size="48px" />
          <VStack align="flex-start" spacing={1}>
            <HStack spacing={4}>
              <Text variant="lg.sb">Approve request</Text>
              <SignedBadge signatures={request.signatures} />
            </HStack>
            <Text fontSize="sm" color="gray.600">
              {request.approval.title}
            </Text>
          </VStack>
        </HStack>
        <Divider />
        <VStack w="full" align="flex-start" spacing={1}>
          <Text variant="label">Request description</Text>
          <Text variant="lil">{request.approval.body}</Text>
        </VStack>
        <Divider />
        <HStack w="full" align="flex-start">
          <VStack w="50%" align="flex-start" spacing={1}>
            <Text variant="label">Requestor</Text>
            <Text variant="lil">{request.requester}</Text>
          </VStack>
          <VStack w="50%" align="flex-start" spacing={1}>
            <Text variant="label">Resource</Text>
            <Text variant="lil">{request.resource}</Text>
          </VStack>
        </HStack>
        <HStack w="full" align="flex-start">
          {!request.lines?.length && (
            <VStack w="50%" align="flex-start" spacing={1}>
              <Text variant="label">Requested for</Text>
              <Text variant="lil">
                {humanDuration(request.requestedForSeconds)}
              </Text>
            </VStack>
          )}
          <VStack w="50%" align="flex-start" spacing={1}>
            <Text variant="label">Requested from</Text>
            <Text variant="lil">{request.requestedFrom}</Text>
          </VStack>
        </HStack>
        {request.requestComments && (
          <VStack w="full" align="flex-start" spacing={1}>
            <Text variant="label">Comments</Text>
            <Text variant="lil">{request.requestComments}</Text>
          </VStack>
        )}
        {request.lines?.length ? (
          <VStack w="full" align="flex-start" spacing={2}>
            <Text variant="label">Roles</Text>
            <Text variant="lil">
              Your decision covers every role. &quot;This stage&quot; marks the
              roles this approval step is for.
            </Text>
            <RequestLines lines={request.lines} showStatus={false} />
          </VStack>
        ) : null}
        <Divider />
        {request.status === "pending" && needsPasskey && (
          <Text variant="lil" color="gray.600">
            Approving this step needs your passkey. You will see what you are
            approving, and a code, before you sign.
          </Text>
        )}
        {request.status === "pending" ? (
          <HStack w="full" justify="flex-end">
            {!denyOpen && !approveOpen ? (
              <>
                <Button
                  size="sm"
                  leftIcon={<Icon as={FiXCircle} />}
                  colorScheme="red"
                  onClick={toggleDeny}
                >
                  Deny
                </Button>
                {needsPasskey ? (
                  <Button
                    size="sm"
                    leftIcon={<Icon as={TbFingerprint} />}
                    colorScheme="green"
                    onClick={onApproveWithPasskey}
                    isLoading={isStarting}
                  >
                    Approve with passkey
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    leftIcon={<Icon as={FiCheckCircle} />}
                    colorScheme="green"
                    onClick={toggleApprove}
                  >
                    Accept
                  </Button>
                )}
              </>
            ) : denyOpen ? (
              <>
                <IconButton
                  variant={"secondary"}
                  size="sm"
                  icon={<Icon as={FiArrowLeft} />}
                  aria-label="back"
                  onClick={toggleDeny}
                />
                <Button
                  size="sm"
                  leftIcon={<Icon as={FiXCircle} />}
                  colorScheme="red"
                  isLoading={isLoading}
                  onClick={() => onDecide("deny")}
                >
                  Confirm Deny
                </Button>
              </>
            ) : (
              <>
                <IconButton
                  variant={"secondary"}
                  size="sm"
                  icon={<Icon as={FiArrowLeft} />}
                  aria-label="back"
                  onClick={toggleApprove}
                />
                <Button
                  size="sm"
                  leftIcon={<Icon as={FiCheckCircle} />}
                  colorScheme="green"
                  onClick={() => onDecide("approve")}
                  isLoading={isLoading}
                >
                  Confirm Approve
                </Button>
              </>
            )}
          </HStack>
        ) : (
          <HStack w="full" justify="flex-end">
            <Text variant="lil">
              This request was <b>{request.approval.decision}</b>{" "}
              {request.approval.decidedAt &&
                formatDistanceToNow(new Date(request.approval.decidedAt), {
                  addSuffix: true,
                })}
            </Text>
          </HStack>
        )}
      </VStack>
    </VStack>
  );
};

export default ApprovalRequest;
